import AutoAwesome from '@mui/icons-material/AutoAwesome';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useMemo } from 'react';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import { PreferencesContent } from 'app/pages/EmailsGridPage/components/PreferencesContent';
import { useEmbeddedTemplateGeneration } from 'app/pages/EmailsGridPage/hooks/useEmbeddedTemplateGeneration';

type EmbeddedTemplateGenerationSectionProps = {
  dealerId: string;
  enabled: boolean;
  /** When set, only the matching segment row is shown in the preferences table. */
  filterSegmentName?: string;
};

export default function EmbeddedTemplateGenerationSection({
  dealerId,
  enabled,
  filterSegmentName,
}: EmbeddedTemplateGenerationSectionProps) {
  const {
    segmentInventories,
    handleSavePreferences,
    handleGenerate,
    preferencesSaveLoading,
    canSavePreferences,
  } = useEmbeddedTemplateGeneration({ dealerId, enabled });

  const {
    segmentRows,
    headerRow,
    loading,
    error,
    expandedCell,
    vehiclesForExpandedCell,
    vehiclesLoading,
    vehiclesError,
    preferredVinsForExpandedCell,
    missingVinsForExpandedCell,
    togglePreferredVin,
    toggleExpanded,
  } = segmentInventories;

  const filteredSegmentRows = useMemo(() => {
    const filter = filterSegmentName?.trim();
    if (!filter) return segmentRows;
    const lower = filter.toLowerCase();
    return segmentRows.filter(
      row => String(row.segment_name ?? '').trim().toLowerCase() === lower,
    );
  }, [filterSegmentName, segmentRows]);

  const segmentFilterLabel = filterSegmentName?.trim() ?? '';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        flex: 1,
        minHeight: 0,
        height: '100%',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, flexShrink: 0 }}>
        Template generation preferences
        {segmentFilterLabel ? ` — ${segmentFilterLabel}` : ''}
      </Typography>

      {!loading && segmentFilterLabel && filteredSegmentRows.length === 0 ? (
        <Alert severity="info" sx={{ flexShrink: 0, borderRadius: 2 }}>
          No preference rows found for segment &quot;{segmentFilterLabel}&quot;.
        </Alert>
      ) : null}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 1.5,
        }}
      >
        <PreferencesContent
          isOpen={enabled}
          segmentRows={filteredSegmentRows}
          headerRow={headerRow}
          loading={loading}
          error={error}
          expandedCell={expandedCell}
          vehiclesForExpandedCell={vehiclesForExpandedCell}
          vehiclesLoading={vehiclesLoading}
          vehiclesError={vehiclesError}
          preferredVinsForExpandedCell={preferredVinsForExpandedCell}
          missingVinsForExpandedCell={missingVinsForExpandedCell}
          onTogglePreferredVin={togglePreferredVin}
          onToggleExpanded={toggleExpanded}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          gap: 1,
          flexShrink: 0,
          pt: 0.5,
        }}
      >
        <ButtonMAIQ
          variant="outlined"
          onClick={() => {
            void handleSavePreferences();
          }}
          disabled={preferencesSaveLoading || !canSavePreferences}
          startIcon={
            preferencesSaveLoading ? (
              <CircularProgress size={16} thickness={5} />
            ) : null
          }
        >
          {preferencesSaveLoading ? 'Saving…' : 'Save Preferences'}
        </ButtonMAIQ>
        <ButtonMAIQ
          variant="contained"
          color="primary"
          onClick={() => {
            void handleGenerate();
          }}
          startIcon={<AutoAwesome fontSize="small" />}
        >
          Generate
        </ButtonMAIQ>
      </Box>
    </Box>
  );
}
